import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useTrendChartContext } from '@resistapp/client/contexts/use-overview-context/trendchart-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import {
  ALL_GENES,
  ReferenceByMAEC,
  ReferenceContinent,
  ReferenceEnvType,
  ReferenceTarget,
} from '@resistapp/common/api-types';
import { L1Targets } from '@resistapp/common/assays';
import { MetricMode, ProcessMode, ReferenceMetric } from '@resistapp/common/types';
import { compact } from 'lodash';
import { useMemo } from 'react';

// Define a type for reference level result
export interface ReferenceLevel {
  value: number | null | undefined;
  continent: ReferenceContinent;
  metricMode: ReferenceMetric;
  target: ReferenceTarget;
  environmentType: ReferenceEnvType;
}

/**
 * Hook to get the reference level value(s) (e.g., European mean) for the current metric
 * @param referenceMode - Optional override for the metric mode to use
 * @param options - Additional options for fetching reference levels
 * @returns The reference level value(s) - currently only returns one value,
 *          but the structure supports returning multiple in the future
 */
export function useOverviewReferenceLevels(referenceMode?: ReferenceMetric): number[] {
  const {
    queryFilters: {
      filters: { selectedTargets },
    },
  } = useSampleDataContext();
  const { continent } = useTrendChartContext();
  const {
    referenceLineData,
    metricMode: contextMetricMode,
    selectedSiteDatum,
    effectiveSiteProcessMode,
  } = useOverviewContext();

  // Use the provided metric mode or fall back to the one from context
  const effectiveReferenceMode: ReferenceMetric = referenceMode ?? contextMetricMode;

  // Determine the parameters for the reference data
  const referenceTarget =
    selectedTargets.length === 1
      ? selectedTargets[0]
      : effectiveReferenceMode === MetricMode.REDUCTION
        ? ALL_GENES
        : L1Targets.ARG;

  const referenceEnvType: ReferenceEnvType | undefined =
    effectiveReferenceMode === MetricMode.REDUCTION
      ? selectedSiteDatum?.environment.type || undefined
      : effectiveSiteProcessMode === ProcessMode.BEFORE
        ? selectedSiteDatum?.environment.subtype || undefined
        : selectedSiteDatum?.environmentAfter?.subtype || undefined;

  const referenceLevel = useMemo(() => {
    // For now, only return a single value (backwards compatibility)
    // In the future, this can return an array of ReferenceLevel objects
    return referenceLineData && continent && referenceEnvType
      ? getReference(effectiveReferenceMode, referenceTarget, referenceEnvType, continent, referenceLineData)
      : undefined;
  }, [referenceLineData, continent, referenceEnvType, effectiveReferenceMode, referenceTarget]);

  return compact([referenceLevel]);
}

// Helper function to get the reference value from reference data
function getReference(
  metricMode: ReferenceMetric,
  assayOrTarget: ReferenceTarget,
  etype: ReferenceEnvType,
  continent: ReferenceContinent,
  referenceData: ReferenceByMAEC,
): number | null | undefined {
  return referenceData[metricMode]?.[assayOrTarget]?.[etype]?.[continent]?.mean;
}

/**
 * Future implementation that would support multiple reference levels
 * Currently unused but demonstrates the structure for the future
 */
export function getMultipleReferences(
  metricMode: ReferenceMetric,
  assayOrTarget: ReferenceTarget,
  etype: ReferenceEnvType,
  continents: ReferenceContinent[],
  referenceData: ReferenceByMAEC,
): ReferenceLevel[] {
  return continents
    .map(continent => ({
      value: referenceData[metricMode]?.[assayOrTarget]?.[etype]?.[continent]?.mean,
      continent,
      metricMode,
      target: assayOrTarget,
      environmentType: etype,
    }))
    .filter(item => item.value !== undefined);
}
